import React from 'react';
import { anchorRef, bindTrigger, usePopupState, bindPopover } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';
import { Form, FormRenderProps } from 'react-final-form';
import { Box, Stack } from '@mui/material';

import {
  TableContainer,
  Button,
  TableHeader,
  SearchForm,
  Typography,
  FilterButton,
  isFiltersSelected,
} from '@occc/ui-design-system';
import { Select } from '@occc-app/design-system';
import { usePaginationParams, deletePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { Command_Center_User_Status } from '@occc/graphql';
import { useQueryParam } from '@occc-app/hooks/useQueryParam';
import { useGetOpenCommerceUsers } from '@occc-app/dataAccessLayer/openCommerceUser/queries';

import { UsersTable } from './UsersTable';
import { messages } from './messages';

import { UserFilterFormValues, UserStatusType } from '../types';

const selectData = [
  { label: 'All', value: 'ALL' },
  { label: 'Enabled', value: Command_Center_User_Status.Enabled },
  { label: 'Disabled', value: Command_Center_User_Status.Disabled },
];

const STATUS_PARAM = 'status';

export const UsersTableContainer: React.FC = () => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });

  const [paginationInfo, onPageChange] = usePaginationParams();
  const [search, setSearch] = useSearchParams();

  const [status, setStatus] = useQueryParam<UserStatusType>(
    (searchParams) => {
      const status = searchParams.get(STATUS_PARAM) || undefined;
      return status as UserStatusType;
    },
    (value, searchParams) => {
      if (value && value !== 'ALL') {
        searchParams.set(STATUS_PARAM, value);
      } else {
        searchParams.delete(STATUS_PARAM);
      }

      deletePaginationParams(searchParams);
    },
  );

  const { loading, error, data, paginationProps } = useGetOpenCommerceUsers({
    paginationInfo,
    onPageChange,
    filter: {
      search,
      statusEquals: status === 'ALL' ? undefined : status,
    },
  });

  const popoverProps = React.useMemo(() => {
    return {
      ...bindPopover({
        ...popupState,
      }),
    };
  }, [popupState]);

  const onSubmit = (values: UserFilterFormValues) => {
    setStatus(values.status);
    popoverProps.onClose();
  };

  const onReset = React.useCallback(() => {
    setStatus(undefined);
    popoverProps.onClose();
  }, [popoverProps, setStatus]);

  const renderActions = () => {
    return (
      <Stack direction='row' alignItems='center' gap={1}>
        <SearchForm onSubmit={({ search }) => setSearch(search)} initialValues={{ search }} />
        <FilterButton
          {...bindTrigger(popupState)}
          ref={anchorRef(popupState)}
          showBadge={isFiltersSelected([status])}
        />
      </Stack>
    );
  };

  const renderForm: FormRenderProps<UserFilterFormValues>['render'] = React.useCallback(
    ({ handleSubmit, form }: FormRenderProps<UserFilterFormValues>) => {
      const reset = () => {
        form.reset();
        onReset();
      };

      return (
        <form onSubmit={handleSubmit}>
          <Box px={4} pt={4}>
            <Typography paragraph>Status</Typography>
            <Select name='status' label='Status' data={selectData} />
          </Box>
          <Box display='flex' justifyContent='flex-end' px={3} py={2}>
            <Button size='small' color='error' onClick={reset}>
              reset
            </Button>
            <Button type='submit' size='small' color='primary'>
              apply
            </Button>
          </Box>
        </form>
      );
    },
    [onReset],
  );

  return (
    <TableContainer>
      <TableHeader title={messages.title} actions={renderActions()} />
      <UsersTable users={data} loading={loading} error={error} paginationProps={paginationProps} />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        PaperProps={{
          sx: {
            maxWidth: 460,
            minWidth: 300,
          },
        }}
      >
        <Form<UserFilterFormValues>
          render={renderForm}
          onSubmit={onSubmit}
          initialValues={{ status: status || 'ALL' }}
        />
      </Popover>
    </TableContainer>
  );
};
